import { Case } from 'dashboard/models/case';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import Action from 'lib/interfaces/Action';

export type InquiryActions =
  | Action<InquiryActionType.ClearInquiry>
  | Action<InquiryActionType.SetInquiry, SetInquiryPayload>
  | Action<InquiryActionType.SetInquiryDetails, SetInquiryDetailsPayload>
  | Action<InquiryActionType.SetInquiryRedactor, SetInquiryRedactorPayload>
  | Action<InquiryActionType.SetInquiryReports, SetInquiryReportsPayload>;

export enum InquiryActionType {
  ClearInquiry = 'inquiry/CLEAR_INQUIRY',
  SetInquiry = 'inquiry/SET_INQUIRY',
  SetInquiryDetails = 'inquiry/SET_INQUIRY_DETAILS',
  SetInquiryRedactor = 'inquiry/SET_INQUIRY_REDACTOR',
  SetInquiryReports = 'inquiry/SET_INQUIRY_REPORTS',
}

export function clearInquiry(): Action<InquiryActionType.ClearInquiry> {
  return { type: InquiryActionType.ClearInquiry };
}

interface SetInquiryPayload {
  inquiry: Inquiry;
}

export function setInquiry(
  inquiry: Inquiry,
): Action<InquiryActionType.SetInquiry, SetInquiryPayload> {
  return {
    payload: {
      inquiry,
    },
    type: InquiryActionType.SetInquiry,
  };
}

interface SetInquiryRedactorPayload {
  redactor: User | undefined;
}

export function setInquiryRedactor(
  redactor: User | undefined,
): Action<InquiryActionType.SetInquiryRedactor, SetInquiryRedactorPayload> {
  return {
    payload: {
      redactor,
    },
    type: InquiryActionType.SetInquiryRedactor,
  };
}

interface SetInquiryDetailsPayload {
  cases: Case[];
  creator: User | undefined;
  devices: Device[];
  documents: Document[];
  environment: Environment;
  inquiryTemplateVersion: InquiryShowViewInquiryTemplateVersion | undefined;
  listItems: ListItem[];
  listMatches: ListMatch[];
  lists: List[];
  redactor: User | undefined;
  reportTemplates: ReportTemplate[];
  reports: Report[];
  reviewer: User | WorkflowRun | WorkflowRunStepAction | undefined;
  reviewerWorkflow: Workflow | undefined;
  reviewerWorkflowRun: WorkflowRun | undefined;
  reviewerWorkflowVersion: WorkflowVersion | undefined;
  selfies: Selfie[];
  sessions: InquirySession[];
  tags: Tag[];
  template: InquiryTemplate | Template;
  verifications: Verification[];
}

export function setInquiryDetails({
  cases,
  creator,
  devices,
  documents,
  environment,
  inquiryTemplateVersion,
  listItems,
  listMatches,
  lists,
  redactor,
  reportTemplates,
  reports,
  reviewer,
  reviewerWorkflow,
  reviewerWorkflowRun,
  reviewerWorkflowVersion,
  selfies,
  sessions,
  tags,
  template,
  verifications,
}: SetInquiryDetailsPayload): Action<
  InquiryActionType.SetInquiryDetails,
  SetInquiryDetailsPayload
> {
  return {
    payload: {
      cases,
      creator,
      devices,
      documents,
      environment,
      inquiryTemplateVersion,
      listItems,
      listMatches,
      lists,
      redactor,
      reportTemplates,
      reports,
      reviewer,
      reviewerWorkflow,
      reviewerWorkflowRun,
      reviewerWorkflowVersion,
      selfies,
      sessions,
      tags,
      template,
      verifications,
    },
    type: InquiryActionType.SetInquiryDetails,
  };
}

interface SetInquiryReportsPayload {
  reports: Report[];
}

export function setInquiryReports(
  reports: Report[],
): Action<InquiryActionType.SetInquiryReports, SetInquiryReportsPayload> {
  return {
    payload: {
      reports,
    },
    type: InquiryActionType.SetInquiryReports,
  };
}
